import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';
import { articles } from '@/mocks/articles';

const SITE_URL = import.meta.env.VITE_SITE_URL;

export default function ArticlePage() {
  const { articleSlug } = useParams<{ articleSlug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [articleSlug]);

  const article = articles.find((a) => a.slug === articleSlug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main>
          <div className="w-full pt-24 pb-14 md:pt-28 md:pb-20">
            <div className="w-full px-4 md:px-6 max-w-3xl mx-auto text-center">
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
                Artigo não encontrado
              </h1>
              <p className="text-foreground-600 mb-6">
                O artigo que você procura não está disponível. Confira outros artigos na página inicial.
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white text-sm font-semibold rounded-md hover:bg-primary-600 transition-colors duration-200"
              >
                <i className="ri-arrow-left-line"></i>
                Voltar ao Início
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    image: article.imageUrl,
    author: {
      '@type': 'Organization',
      name: 'CSSBuySpreadsheet.asia',
      url: SITE_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: 'CSSBuySpreadsheet.asia',
      url: SITE_URL,
      logo: {
        '@type': 'ImageObject',
        url: `${SITE_URL}/favicon.png`,
      },
    },
    datePublished: '2026-06-27',
    dateModified: '2026-06-27',
    inLanguage: 'pt',
    url: `${SITE_URL}/${article.slug}`,
    mainEntityOfPage: `${SITE_URL}/${article.slug}`,
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'CSSBuy Spreadsheet',
        item: SITE_URL,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.title,
        item: `${SITE_URL}/${article.slug}`,
      },
    ],
  };

  return (
    <>
      <JsonLd data={articleSchema} />
      <JsonLd data={breadcrumbSchema} />
      <Navbar />
      <main>
        <article className="w-full pt-24 pb-14 md:pt-28 md:pb-20">
          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
            <div className="mb-8">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors duration-200"
              >
                <i className="ri-arrow-left-line text-base"></i>
                Voltar ao Início
              </Link>
            </div>

            <header className="mb-10">
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  Atualizado em Junho 2026
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  {article.readingTime}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  Equipe CSSBuySpreadsheet.asia
                </span>
              </div>
            </header>

            <div className="w-full h-56 md:h-72 rounded-xl overflow-hidden mb-10">
              <img
                src={article.imageUrl}
                alt={article.imageAlt}
                title={article.title}
                className="w-full h-full object-cover object-center"
                loading="lazy"
              />
            </div>

            <div className="prose-custom space-y-8 text-foreground-700 leading-relaxed text-sm md:text-base">
              {article.sections.map((section, sIdx) => (
                <section key={sIdx}>
                  <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-4">
                    {section.heading}
                  </h2>
                  {section.paragraphs.map((para, pIdx) => (
                    <p key={pIdx} className={pIdx < section.paragraphs.length - 1 ? 'mb-4' : ''}>
                      {para}
                    </p>
                  ))}
                  {section.list && section.list.length > 0 && (
                    section.listType === 'ordered' ? (
                      <ol className="list-decimal pl-5 space-y-2 mt-3">
                        {section.list.map((item, lIdx) => (
                          <li key={lIdx}>{item}</li>
                        ))}
                      </ol>
                    ) : (
                      <ul className="list-disc pl-5 space-y-2 mt-3">
                        {section.list.map((item, lIdx) => (
                          <li key={lIdx}>{item}</li>
                        ))}
                      </ul>
                    )
                  )}
                </section>
              ))}
            </div>

            <div className="mt-12 pt-8 border-t border-background-200">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white text-sm font-semibold whitespace-nowrap rounded-md hover:bg-primary-600 transition-colors duration-200"
                >
                  <i className="ri-arrow-left-line"></i>
                  Voltar ao Início
                </Link>
                <div className="flex flex-wrap gap-2">
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="px-4 py-2 text-sm font-medium text-primary-600 border border-primary-200 rounded-md hover:bg-primary-50 transition-colors duration-200 whitespace-nowrap"
                  >
                    CSSBuy Sheet
                  </a>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="px-4 py-2 text-sm font-medium text-primary-600 border border-primary-200 rounded-md hover:bg-primary-50 transition-colors duration-200 whitespace-nowrap"
                  >
                    Buy It Now
                  </a>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}